<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseBubble from './Base.vue';
import Icon from 'next/icon/Icon.vue';
import { useMessageContext } from '../provider.js';

const { contentAttributes } = useMessageContext();
const { t } = useI18n();

const pollData = computed(() => {
  const attrs = contentAttributes.value || {};
  return {
    question: attrs.question || attrs.poll_question || attrs.name || '',
    options: attrs.options || attrs.poll_options || [],
    maxAnswers: attrs.max_answers || attrs.maxAnswers || 1,
    votes: attrs.poll_votes || attrs.votes || {},
  };
});

const totalVotes = computed(() => {
  const votes = pollData.value.votes;
  if (!votes || typeof votes !== 'object') return 0;

  // Count unique voters
  return Object.keys(votes).length;
});

const optionsWithVotes = computed(() => {
  const { options, votes } = pollData.value;
  if (!options || !Array.isArray(options)) return [];

  // Count votes per option
  const voteCounts = {};
  options.forEach((_, index) => {
    voteCounts[index] = 0;
  });

  // Count votes from poll_votes object
  if (votes && typeof votes === 'object') {
    Object.values(votes).forEach(voteData => {
      const selectedOptions =
        voteData.options || voteData.selectedOptions || [];
      selectedOptions.forEach(optIndex => {
        if (voteCounts[optIndex] !== undefined) {
          voteCounts[optIndex] += 1;
        }
      });
    });
  }

  return options.map((option, index) => ({
    text:
      typeof option === 'string' ? option : option.text || option.name || '',
    votes: voteCounts[index] || 0,
    percentage:
      totalVotes.value > 0
        ? Math.round((voteCounts[index] / totalVotes.value) * 100)
        : 0,
  }));
});
</script>

<template>
  <BaseBubble
    class="overflow-hidden p-4 min-w-64 max-w-sm"
    data-bubble-name="poll"
  >
    <div class="space-y-3">
      <!-- Header -->
      <div class="flex items-center gap-2">
        <div class="size-8 rounded-lg bg-woot-500/10 grid place-content-center">
          <Icon
            icon="i-ph-chart-bar-horizontal-fill"
            class="text-woot-500 size-4"
          />
        </div>
        <div class="flex-1 min-w-0">
          <span class="text-xs text-n-slate-11">
            {{ t('CONVERSATION.POLL.TITLE') }}
          </span>
        </div>
      </div>

      <!-- Question -->
      <div class="text-n-slate-12 font-medium">
        {{ pollData.question }}
      </div>

      <!-- Options -->
      <div class="space-y-2">
        <div
          v-for="(option, index) in optionsWithVotes"
          :key="index"
          class="relative overflow-hidden rounded-lg bg-n-alpha-2 p-2"
        >
          <!-- Progress bar -->
          <div
            class="absolute inset-y-0 left-0 bg-woot-500/20 transition-all duration-300"
            :style="{ width: `${option.percentage}%` }"
          />

          <!-- Option content -->
          <div class="relative flex items-center justify-between gap-2">
            <span class="text-sm text-n-slate-12 truncate">
              {{ option.text }}
            </span>
            <div class="flex items-center gap-1 shrink-0">
              <span class="text-xs text-n-slate-11">
                {{ option.votes }}
              </span>
              <span
                v-if="option.percentage > 0"
                class="text-xs text-n-slate-10"
              >
                ({{ option.percentage }}%)
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="flex items-center justify-between text-xs text-n-slate-11 pt-1 border-t border-n-weak"
      >
        <span>
          {{ t('CONVERSATION.POLL.TOTAL_VOTES', { count: totalVotes }) }}
        </span>
        <span v-if="pollData.maxAnswers > 1">
          {{
            t('CONVERSATION.POLL.MAX_ANSWERS', { count: pollData.maxAnswers })
          }}
        </span>
      </div>
    </div>
  </BaseBubble>
</template>
